import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SEO_DEFAULTS } from "@/data/content";

/**
 * Document-level SEO helper.
 * Renders title/description/OG/Twitter via Helmet, and imperatively manages
 *   - <link rel="canonical">
 *   - <script type="application/ld+json" data-seo-jsonld="true">
 * because react-helmet-async + React 19 drops <link> and <script> children
 * from <Helmet> at runtime. Title/meta/OG/Twitter still flow through Helmet.
 */
export default function Seo({
  title,
  description,
  path = "",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} · ${SEO_DEFAULTS.siteName}`
    : `${SEO_DEFAULTS.siteName} — Global Consulting & Technology Group`;
  const desc = description || SEO_DEFAULTS.defaultDescription;
  const url = `${SEO_DEFAULTS.baseUrl}${path}`;
  const ogImage = image || SEO_DEFAULTS.defaultImage;

  // Imperative canonical link
  useEffect(() => {
    if (typeof document === "undefined") return;
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", "canonical");
      document.head.appendChild(el);
    }
    el.setAttribute("href", url);
  }, [url]);

  // Imperative JSON-LD <script> injection
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null;
  useEffect(() => {
    if (typeof document === "undefined") return;
    let el = document.querySelector('script[data-seo-jsonld="true"]');
    if (!jsonLdString) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
      return;
    }
    if (!el) {
      el = document.createElement("script");
      el.setAttribute("type", "application/ld+json");
      el.setAttribute("data-seo-jsonld", "true");
      document.head.appendChild(el);
    }
    el.textContent = jsonLdString;
    return () => {
      // On unmount, leave the latest one in place; the next route's effect will overwrite.
    };
  }, [jsonLdString]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
